import { Check, Mic, Music, Zap, type LucideIcon } from 'lucide-react';
import { motion } from 'motion/react';

interface HeroBadge {
  label: string;
  Icon: LucideIcon;
  iconClassName: string;
}

const HERO_BADGES: HeroBadge[] = [
  { label: 'На базе Suno', Icon: Music, iconClassName: 'text-purple-600' },
  {
    label: 'Генерация до 2 минут',
    Icon: Zap,
    iconClassName: 'text-yellow-600',
  },
  {
    label: 'Профессиональный вокал',
    Icon: Mic,
    iconClassName: 'text-pink-600',
  },
];

interface StepStartProps {
  agreed: boolean;
  onAgreeChange: (agreed: boolean) => void;
  disabled?: boolean;
  onVoiceSong: () => void;
  voiceSongHasBalance: boolean;
  createSongHasBalance: boolean;
  onCreateSong: () => void;
  onOpenPrivacy: () => void;
  onOpenTerms: () => void;
}

export function StepStart({
  agreed,
  onAgreeChange,
  disabled = false,
  onVoiceSong,
  voiceSongHasBalance,
  createSongHasBalance,
  onCreateSong,
  onOpenPrivacy,
  onOpenTerms,
}: StepStartProps) {
  const actionsDisabled = !agreed || disabled;

  return (
    <motion.section
      aria-labelledby="start-title"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      // `isolate` создаёт собственный stacking context, иначе фон с -z-10
      // может уйти под градиент родителя.
      className="relative isolate w-full max-w-3xl text-center"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10 rounded-3xl bg-[url('/container.png')] bg-cover bg-center opacity-20"
      />

      <h1
        id="start-title"
        className="bg-[linear-gradient(90deg,#9810fa_0%,#e60076_50%,#155dfc_100%)] bg-clip-text text-3xl font-bold leading-tight tracking-[0.35px] text-transparent sm:text-4xl md:text-5xl md:leading-[60px] md:whitespace-nowrap"
      >
        Создание песен нейросетью
      </h1>

      <p className="mx-auto max-w-3xl py-2.5 text-base leading-6 text-[#1e2939]">
        Расскажите, что хотите выразить, — нейросеть превратит идею в песню с
        музыкой и вокалом. Первую генерацию можно попробовать бесплатно
      </p>

      <ul
        aria-label="Возможности генерации"
        className="flex flex-wrap items-start justify-center gap-1.5 pb-8 pt-2.5 sm:pb-12"
      >
        {HERO_BADGES.map(({ label, Icon, iconClassName }) => (
          <li
            key={label}
            className="inline-flex items-center gap-2 rounded-[14px] border border-solid border-[#bb09be33] bg-white/80 px-4 py-2 backdrop-blur-sm"
          >
            <Icon
              aria-hidden="true"
              className={`h-5 w-5 shrink-0 ${iconClassName}`}
            />
            <span className="whitespace-nowrap text-sm font-bold leading-5 tracking-[-0.15px] text-[#1e2939]">
              {label}
            </span>
          </li>
        ))}
      </ul>

      <div className="flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4">
        <motion.button
          type="button"
          whileHover={actionsDisabled ? undefined : { scale: 1.03 }}
          whileTap={actionsDisabled ? undefined : { scale: 0.97 }}
          onClick={onVoiceSong}
          disabled={actionsDisabled}
          className="flex h-20 w-full max-w-[325px] flex-col items-center justify-center rounded-full bg-[linear-gradient(90deg,#22c55e_0%,#06b6d4_100%)] px-4 text-2xl font-bold leading-7 tracking-[0.07px] text-white shadow-[0px_25px_50px_#0ebab24c] disabled:cursor-not-allowed disabled:opacity-60 sm:w-[325px]"
        >
          <span>Песня своим голосом</span>
          {!voiceSongHasBalance && (
            <span className="text-sm font-medium leading-5">(купить за 299р)</span>
          )}
        </motion.button>
        <motion.button
          type="button"
          whileHover={actionsDisabled ? undefined : { scale: 1.03 }}
          whileTap={actionsDisabled ? undefined : { scale: 0.97 }}
          onClick={onCreateSong}
          disabled={actionsDisabled}
          className="flex h-20 w-full max-w-[262px] flex-col items-center justify-center rounded-full bg-[linear-gradient(90deg,#9810fa_0%,#e60076_100%)] px-4 text-2xl font-bold leading-7 tracking-[0.07px] text-white opacity-[0.92] shadow-[0px_25px_50px_-12px_#ad46ff4c] disabled:cursor-not-allowed disabled:opacity-60 sm:w-[262px]"
        >
          <span>Обычная песня</span>
          {!createSongHasBalance && (
            <span className="text-sm font-medium leading-5">(купить за 199р)</span>
          )}
        </motion.button>
      </div>

      <div className="mt-8 flex items-start justify-center gap-2 sm:mt-[60px]">
        <label className="relative mt-0.5 inline-flex shrink-0 cursor-pointer">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(event) => onAgreeChange(event.target.checked)}
            className="peer sr-only"
            aria-label="Согласие с политикой конфиденциальности и пользовательским соглашением"
          />
          <span
            aria-hidden="true"
            className={`flex h-5 w-5 items-center justify-center rounded-sm border border-solid border-[#9810fa] transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-[#9810fa] peer-focus-visible:ring-offset-2 ${
              agreed ? 'bg-[#9810fa]' : 'bg-white'
            }`}
          >
            {agreed && <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />}
          </span>
        </label>

        <p className="text-left text-sm font-medium leading-6 tracking-[-0.15px] text-[#364153]">
          Я согласен с{' '}
          <button
            type="button"
            onClick={onOpenPrivacy}
            className="text-base font-medium tracking-[-0.31px] text-[#9810fa] hover:underline"
          >
            политикой конфиденциальности
          </button>{' '}
          и{' '}
          <button
            type="button"
            onClick={onOpenTerms}
            className="text-base font-medium tracking-[-0.31px] text-[#9810fa] hover:underline"
          >
            пользовательским соглашением
          </button>
        </p>
      </div>
    </motion.section>
  );
}
